import { map } from 'lodash-es';
import { computed } from 'vue';
import Product from '@maison/components/common/Product';
import MixedGrid from '@phoenix/mixins/components/mixedGrid/MixedGrid';
import MixedGridContentItem from '@maison/components/mixedGrid/MixedGridContentItem';
import MixedGridPagePushItem from '@maison/components/mixedGrid/MixedGridPagePushItem';
import MixedGridImageItem from '@maison/components/mixedGrid/MixedGridImageItem';
import MixedGridVideoItem from '@maison/components/mixedGrid/MixedGridVideoItem';
import { getLocalizedPriceRangesFilterItems } from '@phoenix/helpers/facet-helper';

export default {
  mixins: [MixedGrid],

  provide() {
    return {
      customFilters: computed(() => this.customFilters),
    };
  },

  props: {
    alignedCenter: {
      type: Boolean,
      default: false,
    },
    ctas: {
      type: Array,
      default: () => [],
    },
    customFilters: {
      type: Object,
      default: () => ({}),
    },
    filterPriceConfig: {
      type: Object,
      default: () => ({}),
    },
    headingTag: {
      type: String,
      default: 'h2',
    },
    relatedLinks: {
      type: Array,
      default: () => [],
    },
    shouldHideMediaItemsOnMobile: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      pageInnerWidth: window.innerWidth,
    };
  },

  computed: {
    hasPriceFilter() {
      return this.filterPriceConfig?.ranges?.length > 0;
    },

    allFilters() {
      if (!this.shouldDisplaySellable) {
        return this.filters;
      }

      const availableOnlineFilter = {
        id: 'sellable',
        label: this.$t('Products.filters.availableOnline'),
        component: 'list',
        items: [
          {
            value: 'sellable',
            valueName: 'sellable',
            label: this.$t('Products.filters.availableOnline'),
            trackingLabel: 'available-online-true',
          },
        ],
      };

      return [availableOnlineFilter, ...this.filters];
    },

    filtersWithLocalizedPrices() {
      if (!this.hasPriceFilter || !this.filterPriceConfig) {
        return this.allFilters;
      }

      const priceItems = getLocalizedPriceRangesFilterItems(
        this.filterPriceConfig.ranges,
        this.filterPriceConfig.countriesWithCurrency,
      );

      if (priceItems.length === 0) {
        return this.allFilters;
      }

      const filterPrice = {
        id: 'hasprice',
        label: this.$t('MixedGrid.filters.price'),
        trackingLabel: 'hasprice',
        uniqueValue: true,
        hasRange: true,
        component: 'list',
        items: priceItems,
      };

      return [...this.allFilters, filterPrice];
    },

    shouldDisplayLoadMore() {
      return this.hasLoadMore && !this.areSortOrFiltersTouched && this.page < this.maxPage[this.pageSize];
    },

    pageSize() {
      if (this.pageInnerWidth >= 1025) {
        return 'lg';
      }
      if (this.pageInnerWidth >= 745) {
        return 'md';
      }
      if (this.pageInnerWidth >= 516) {
        return 'sm';
      }
      return 'xs';
    },

    /**
     * Available widths for each breakpoints
     */
    widths() {
      return {
        lg: {
          1: '1',
          2: '2',
          4: '4',
        },
        md: {
          1: '1',
          2: '2',
        },
        sm: {
          1: '1',
          2: '2',
        },
        xs: {
          1: '1',
          2: '2',
        },
      };
    },
  },

  watch: {
    activeFilters: {
      handler(newFilters) {
        if (Object.entries(newFilters).length > 0) {
          this.areFiltersTouched = true;
          this.areSortOrFiltersTouched = true;
          return;
        }

        this.areFiltersTouched = false;
        this.areSortOrFiltersTouched = false;
      },
      deep: 2,
    },
  },

  created() {
    window.addEventListener('resize', () => (this.pageInnerWidth = window.innerWidth));
  },

  methods: {
    getComponent(template) {
      switch (template) {
        case 'product':
          return Product;
        case 'image-fit':
          return MixedGridImageItem;
        case 'video-item':
          return MixedGridVideoItem;
        case 'push-item':
        case 'push-video-item':
          return MixedGridPagePushItem;
        case 'content-item':
          return MixedGridContentItem;
      }
    },

    getDisplayedColumnClasses(column) {
      if (!this.shouldDisplayLoadMore) {
        return;
      }

      return map(column.page, (page, size) => {
        const prefix = size !== 'xs' ? `${size}:` : '';
        return this.page < page ? `${prefix}hidden` : '';
      });
    },

    /**
     * Construct the classes used to define the sizes of columns for each breakpoints.
     */
    getSizes(column) {
      const prefix = 'mixed-grid__item';

      if (this.areSortOrFiltersTouched) {
        return `${prefix}--1/2 lg:${prefix}--1/4`;
      }

      const lg = column.sizes.lg ? column.sizes.lg : 1;
      const xs = column.sizes.xs ? column.sizes.xs : 1;

      return `${prefix}--${this.widths.xs[xs]}/2 lg:${prefix}--${this.widths.lg[lg]}/4`;
    },

    getColumnClasses(column) {
      return [
        'mixed-grid__item',
        this.getSizes(column),
        {
          'sm:hidden-down': column.template !== 'product' && this.shouldHideMediaItemsOnMobile,
        },
        this.getDisplayedColumnClasses(column),
      ];
    },

    getColumnData(column) {
      const data = { ...column.data };
      if (column.template === 'product') {
        data.disableWideStyle = this.areSortOrFiltersTouched;
        data.dataTracking = this.getDataTracking(data);
      }
      return data;
    },
  },
};
